import React from 'react';
import { limitAsNumber, formatEntitlementLimit, canUpgradeKeywordLimit } from '../../../../../../core/utility';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Plus, X, Check, ChevronDown, ChevronRight, Shuffle, Lock, Link2, FileText } from 'lucide-react';
import { ZapLinkProduct, AutomationRuleEntry } from 'custom-type';
import { ZapLinkSection } from './ZapLinkSection';
import { InstagramDMAutomation } from '../../../../../../core/services/dm-automation-instagram.service';
import { ZapPreview } from './ZapPreview';
import {
  ZapGoalTemplate,
  ZapGoalQuestion,
  ZapGoalAnswers,
  ZapRecapField,
  buildPreviewSteps,
  previewAudienceNote,
  recapFields,
  recapFieldValue,
  repliesRotationNote,
  summariseGoalRule,
  FORM_FIELD_PRESETS,
  newCustomFormField,
  ZapFormFieldDraft,
  OPENER_MESSAGE_SUGGESTIONS,
  OPENER_BUTTON_SUGGESTIONS,
  FOLLOW_ASK_SUGGESTIONS,
  MESSAGE_SUGGESTIONS,
  SIGNUP_BUTTON_SUGGESTIONS,
  DEFAULT_SIGNUP_BUTTON,
  DEFAULT_DM_MESSAGE,
} from '../zapGoalTemplates';

/**
 * One question at a time, with the resulting conversation drawn underneath.
 *
 * The wizard owns the answers and the question index (its footer Next drives
 * them); this component only renders the question it is handed.
 *
 * Two deliberate restraints:
 *   - one control per screen, and no jargon in the question — the preview does
 *     the explaining, so nothing has to be described in words first;
 *   - the full rule form is reachable only from the final recap, not from every
 *     question, so "Customise" stops competing with the answer being asked for.
 */

interface StepGoalSetupProps {
  template: ZapGoalTemplate;
  question: ZapGoalQuestion;
  questionNumber: number;
  questionCount: number;
  /** Every step in this goal, so the stepper can name what is coming. */
  questions: ZapGoalQuestion[];
  /** Highest step reached — earlier ones are tappable, later ones are not. */
  maxVisitedIndex: number;
  onJumpToQuestion: (index: number) => void;

  answers: ZapGoalAnswers;
  setAnswers: React.Dispatch<React.SetStateAction<ZapGoalAnswers>>;
  entitlements: any;

  /** Owner ids used to suggest the creator's own storefront / bio page as buttons. */
  ctaOwner?: { influencer_id: number | string; account_id: number | string };
  /** Building a form in ClanConnect is a paid feature; a borrowed link never is. */
  canBuildForms: boolean;
  handleGoToUpgrade: () => void;
  /** Zap Link products, for the 'sell' template's product question. */
  zapLinks: ZapLinkProduct[];
  setZapLinks: (links: ZapLinkProduct[]) => void;
  maxZapProducts: number;
  handleOpenUpgradePopup: (content: React.ReactNode) => void;
  onClose: () => void;
  currentPlanName: string | null;
  allowOtherSource: boolean;
  zapProductImageUploadPath: string;

  /** Hands over to the full rule form with everything answered so far filled in. */
  onCustomise: () => void;
  /** Plain-English account of what will be created — shown on the recap. */
  summaryLines: string[];
  /** Starts a second keyword -> response rule inside the same Zap. */
  onAddAnotherResponse: () => void;
  /** How many responses this Zap already has, including the one being built. */
  ruleCount: number;
  /** Which response the recap is showing, and how to move between them. */
  activeResponse: number;
  onSelectResponse: (index: number) => void;
  onRemoveResponse: (index: number) => void;
  /** Plain-English summary of every response, for the carousel. */
  responseSummaries: string[][];
}


/* Tap-to-fill presets — the same ones the full form offered, so a default is
   always one tap from being replaced rather than something to retype. */
function ChipPicker({ options, onPick }: { options: string[]; onPick: (value: string) => void }) {
  if (!options.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-gray-400">Try:</span>
      {options.map((option) => (
        <button
          key={option}
          type="button"
          onClick={() => onPick(option)}
          className="max-w-full truncate rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-600 hover:border-purple-300"
        >
          {option}
        </button>
      ))}
    </div>
  );
}

/* One editable line on the recap: label + current value, expanding in place.
   Nothing opens a new screen or a modal — the recap stays the one place where
   the whole Zap can be seen and changed. */
function RecapRow({
  field,
  answers,
  setAnswers,
}: {
  field: ZapRecapField;
  answers: ZapGoalAnswers;
  setAnswers: React.Dispatch<React.SetStateAction<ZapGoalAnswers>>;
}) {
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState('');

  const addReply = (text: string) => {
    const value = text.trim();
    if (!value) return;
    setAnswers((prev) => ({ ...prev, comment_replies: [...prev.comment_replies, value] }));
    setDraft('');
  };

  const addKeyword = (text: string) => {
    const value = text.trim();
    if (!value) return;
    setAnswers((prev) =>
      prev.keywords.some((k) => k.toLowerCase() === value.toLowerCase())
        ? prev
        : { ...prev, keywords: [...prev.keywords, value] }
    );
    setDraft('');
  };

  return (
    <div className="border-b border-gray-100 py-2 last:border-b-0">
      <div
        role="button"
        onClick={() => setOpen((o) => !o)}
        className="flex cursor-pointer items-start gap-2"
      >
        <div className="min-w-0 flex-1">
          <p className="mb-0 text-[12px] font-semibold text-[#111]">{field.label}</p>
          <p className="mb-0 truncate text-[12px] text-gray-500">{recapFieldValue(field, answers)}</p>
        </div>
        {open ? (
          <ChevronDown className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
        ) : (
          <ChevronRight className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
        )}
      </div>

      {open && (
        <div className="mt-2 space-y-2">
          {field.kind === 'chips' && (
            <>
              <div className="flex flex-wrap gap-2">
                {answers.keywords.map((k) => (
                  <span
                    key={k}
                    className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-[12px] text-gray-700"
                  >
                    {k}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() =>
                        setAnswers((prev) => ({
                          ...prev,
                          keywords: prev.keywords.filter((x) => x !== k),
                        }))
                      }
                    />
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={draft} placeholder="Add another word" onChange={(e) => setDraft(e.target.value)} />
                <button
                  type="button"
                  onClick={() => addKeyword(draft)}
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-black text-white"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </>
          )}

          {field.kind === 'replies' && (
            <>
              {answers.comment_replies.filter((r) => r.trim()).map((r, i) => (
                <div key={i} className="flex items-start gap-2 rounded-lg bg-gray-50 px-3 py-2">
                  <p className="mb-0 flex-1 text-[12px] text-gray-700">{r}</p>
                  <X
                    className="mt-[2px] h-3 w-3 flex-shrink-0 cursor-pointer text-gray-400"
                    onClick={() =>
                      setAnswers((prev) => ({
                        ...prev,
                        comment_replies: prev.comment_replies.filter((_, idx) => idx !== i),
                      }))
                    }
                  />
                </div>
              ))}
              <div className="flex gap-2">
                <Input value={draft} placeholder="Add a reply" onChange={(e) => setDraft(e.target.value)} />
                <button
                  type="button"
                  onClick={() => addReply(draft)}
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-black text-white"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              {!!field.suggestions?.length && (
                <div className="flex flex-wrap gap-2">
                  {field.suggestions.map((sug) => (
                    <button
                      key={sug}
                      type="button"
                      onClick={() => addReply(sug)}
                      className="rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] text-gray-600 hover:border-purple-300"
                    >
                      {sug}
                    </button>
                  ))}
                </div>
              )}
              {!!repliesRotationNote(answers.comment_replies) && (
                <p className="mb-0 text-[11px] text-gray-500">
                  {repliesRotationNote(answers.comment_replies)}
                </p>
              )}
            </>
          )}

          {field.kind === 'text' && (
            <>
              <Textarea
                rows={3}
                value={
                  field.id === 'message'
                    ? answers.message
                    : field.id === 'opener_message'
                    ? answers.opener_message
                    : answers.follow_ask_message
                }
                onChange={(e) =>
                  setAnswers((prev) => ({ ...prev, [field.id]: e.target.value } as ZapGoalAnswers))
                }
              />
              {!!field.suggestions?.length && (
                <div className="flex flex-wrap gap-2">
                  {field.suggestions.map((sug) => (
                    <button
                      key={sug}
                      type="button"
                      onClick={() => setAnswers((prev) => ({ ...prev, [field.id]: sug } as ZapGoalAnswers))}
                      className="max-w-full truncate rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] text-gray-600 hover:border-purple-300"
                    >
                      {sug}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {field.kind === 'short' && (
            <>
              <Input
                value={answers.opener_button_label}
                maxLength={field.maxLength}
                onChange={(e) => setAnswers((prev) => ({ ...prev, opener_button_label: e.target.value }))}
              />
              {!!field.suggestions?.length && (
                <div className="flex flex-wrap gap-2">
                  {field.suggestions.map((sug) => (
                    <button
                      key={sug}
                      type="button"
                      onClick={() => setAnswers((prev) => ({ ...prev, opener_button_label: sug }))}
                      className="rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] text-gray-600 hover:border-purple-300"
                    >
                      {sug}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {field.kind === 'form_summary' && (
            <div className="rounded-lg bg-gray-50 px-3 py-2">
              <p className="mb-1 text-[12px] font-semibold text-[#111]">
                {answers.form_title.trim() || 'Untitled form'}
              </p>
              {answers.form_fields.map((f) => (
                <p key={f.key} className="mb-0 text-[11px] text-gray-500">
                  • {f.label}{f.required ? ' (required)' : ''}
                </p>
              ))}
              <p className="mb-0 mt-1.5 text-[11px] text-gray-400">
                Tap Details in the steps above to change these.
              </p>
            </div>
          )}

          {field.kind === 'link' && (
            <>
              <Input
                value={answers.link_url}
                placeholder="https://"
                onChange={(e) => setAnswers((prev) => ({ ...prev, link_url: e.target.value }))}
              />
              {!!answers.link_url.trim() && (
                <Input
                  value={answers.link_label}
                  maxLength={field.maxLength}
                  placeholder="Button label, e.g. Open link"
                  onChange={(e) => setAnswers((prev) => ({ ...prev, link_label: e.target.value }))}
                />
              )}
              {!!answers.link_url.trim() && !/^https?:\/\/\S+$/i.test(answers.link_url.trim()) && (
                <p className="mb-0 text-xs text-red-500">Links need to start with http:// or https://</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function StepGoalSetup({
  template,
  question,
  questionNumber,
  questionCount,
  questions,
  maxVisitedIndex,
  onJumpToQuestion,
  answers,
  setAnswers,
  entitlements,
  ctaOwner,
  canBuildForms,
  handleGoToUpgrade,
  zapLinks,
  setZapLinks,
  maxZapProducts,
  handleOpenUpgradePopup,
  onClose,
  currentPlanName,
  allowOtherSource,
  zapProductImageUploadPath,
  onCustomise,
  summaryLines,
  onAddAnotherResponse,
  ruleCount,
  activeResponse,
  onSelectResponse,
  onRemoveResponse,
  responseSummaries,
}: StepGoalSetupProps) {
  const [keywordDraft, setKeywordDraft] = React.useState('');
  const [replyDraft, setReplyDraft] = React.useState('');
  const [questionDraft, setQuestionDraft] = React.useState('');
  const isReview = question.kind === 'review';
  // A follow-gated rule opens with the "please follow me" ask; everything else
  // opens with the plain invitation card.
  const isFollowCard = answers.follow_gate !== 'off';
  const linkLooksWrong =
    !!answers.link_url.trim() && !/^https?:\/\/\S+$/i.test(answers.link_url.trim());

  const addReply = (raw: string) => {
    const value = raw.trim();
    if (!value) return;
    setAnswers((prev) =>
      prev.comment_replies.some((r) => r.trim().toLowerCase() === value.toLowerCase())
        ? prev
        : { ...prev, comment_replies: [...prev.comment_replies.filter((r) => r.trim()), value] }
    );
    setReplyDraft('');
  };

  /* The creator's own storefront / link-in-bio, offered as ready-made buttons.
     Fetched once, only on the product step, and silently: the manual fields
     work perfectly well without suggestions. */
  const [ctaSuggestions, setCtaSuggestions] = React.useState<
    Array<{ type: string; label: string; name: string; url: string }>
  >([]);
  const ctaLoaded = React.useRef(false);
  React.useEffect(() => {
    if (question.kind !== 'products' || ctaLoaded.current) return;
    if (!ctaOwner?.influencer_id || !ctaOwner?.account_id) return;
    ctaLoaded.current = true;
    new InstagramDMAutomation()
      .getZapCtaSuggestions({ influencer_id: ctaOwner.influencer_id, account_id: ctaOwner.account_id })
      .then((res: any) => setCtaSuggestions(res?.data?.suggestions || []))
      .catch(() => setCtaSuggestions([]));
  }, [question.kind, ctaOwner]);

  const applyCtaSuggestion = (slot: 1 | 2, sug: { label: string; url: string }) =>
    setAnswers((prev) =>
      slot === 1
        ? { ...prev, zap_cta_label: sug.label.slice(0, 20), zap_cta_url: sug.url }
        : { ...prev, zap_cta2_label: sug.label.slice(0, 20), zap_cta2_url: sug.url }
    );

  const ctaFirstComplete = !!answers.zap_cta_label.trim() && !!answers.zap_cta_url.trim();

  const previewSteps = React.useMemo(
    () => buildPreviewSteps(template, answers, { entitlements, zapLinks }),
    [template, answers, entitlements, zapLinks]
  );

  // The plan's keyword cap. -1 (Unlimited) maps to Infinity, so the comparison
  // below needs no special case.
  const maxKeywords = limitAsNumber(entitlements?.max_keywords_per_rule, Infinity);
  const atKeywordLimit = answers.keywords.length >= maxKeywords;

  const addKeyword = (raw: string) => {
    const value = raw.trim().replace(/\s+/g, ' ');
    if (!value) return;
    setAnswers((prev) => {
      if (prev.keywords.some((k) => k.toLowerCase() === value.toLowerCase())) return prev;
      // OLD: appended with no cap at all. StepConfigureRules — the advanced
      // editor this guided flow replaced — has always enforced
      // max_keywords_per_rule, but the check did not come across when the
      // simplified flow was built.
      if (prev.keywords.length >= maxKeywords) return prev;
      return { ...prev, keywords: [...prev.keywords, value] };
    });
    setKeywordDraft('');
  };

  const removeKeyword = (value: string) => {
    setAnswers((prev) => ({ ...prev, keywords: prev.keywords.filter((k) => k !== value) }));
  };

  return (
    <div className="space-y-4">
      {/* The whole journey, named. Anonymous dots told you how far along you were
          but not what was coming, so a step you had not reached yet — the public
          reply — looked like a step that did not exist. Anything already reached
          is tappable, so going back to change an answer costs one tap. */}
      <div className="-mx-1 flex items-center gap-1 overflow-x-auto px-1 pb-1" style={{ scrollbarWidth: 'none' }}>
        {questions.map((q, i) => {
          const current = i === questionNumber - 1;
          const reachable = i <= maxVisitedIndex;
          return (
            <React.Fragment key={q.id + i}>
              {i > 0 && <span className="h-px w-2 flex-shrink-0 bg-gray-200" />}
              <button
                type="button"
                disabled={!reachable}
                onClick={() => reachable && onJumpToQuestion(i)}
                className={`flex-shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all ${
                  current
                    ? 'bg-[#7c3aed] text-white'
                    : reachable
                    ? 'bg-[#f5f3ff] text-[#7c3aed] hover:bg-[#ede9fe]'
                    : 'bg-gray-100 text-gray-400'
                }`}
              >
                {q.stepLabel}
              </button>
            </React.Fragment>
          );
        })}
      </div>

      <div>
        <h3 className="mb-1">{question.title}</h3>
        {question.help && <p className="text-sm text-gray-500">{question.help}</p>}
      </div>

      {/* ---------------- keywords ---------------- */}
      {question.kind === 'keywords' && (
        <div className="space-y-3">
          {/* Tap-to-pick first: most people never need the text field at all. */}
          {!!question.suggestions?.length && (
            <div className="flex flex-wrap gap-2">
              {question.suggestions.map((s) => {
                const picked = answers.keywords.some((k) => k.toLowerCase() === s.toLowerCase());
                // At the cap an unpicked chip can do nothing — show that rather
                // than letting it look tappable and do nothing.
                const blocked = !picked && atKeywordLimit;
                return (
                  <button
                    key={s}
                    type="button"
                    disabled={blocked}
                    onClick={() => (picked ? removeKeyword(s) : addKeyword(s))}
                    className={`rounded-full border px-4 py-2 text-[13px] font-semibold transition-all ${
                      picked
                        ? 'border-[#7c3aed] bg-[#f5f3ff] text-[#7c3aed]'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-purple-300'
                    } ${blocked ? 'cursor-not-allowed opacity-40' : ''}`}
                  >
                    {picked && <Check className="mr-1 inline h-3 w-3" />}
                    {s}
                  </button>
                );
              })}
            </div>
          )}

          <div className="flex gap-2">
            <Input
              value={keywordDraft}
              placeholder={atKeywordLimit ? 'Word limit reached' : 'or type your own word'}
              disabled={atKeywordLimit}
              onChange={(e) => setKeywordDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addKeyword(keywordDraft);
                }
              }}
            />
            <button
              type="button"
              disabled={atKeywordLimit}
              onClick={() => addKeyword(keywordDraft)}
              className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-black text-white ${
                atKeywordLimit ? 'cursor-not-allowed opacity-40' : ''
              }`}
              aria-label="Add word"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>

          {/* Says what the cap is and offers the way past it, rather than
              leaving the + button mysteriously inert. */}
          {/* OLD: this always offered an "upgrade" link. At 10 words the creator
              is already on the product's ceiling — every paid plan caps at 10 —
              so the link led to a page that could not give them a single extra
              word. Only offered when a higher cap genuinely exists. */}
          {atKeywordLimit && Number.isFinite(maxKeywords) && (
            <p className="mb-0 text-[12px] text-gray-500">
              Your plan covers {formatEntitlementLimit(entitlements?.max_keywords_per_rule)} word
              {maxKeywords === 1 ? '' : 's'} per Zap
              {canUpgradeKeywordLimit(entitlements) ? (
                <>
                  . Remove one to swap it, or{' '}
                  <span
                    className="cursor-pointer font-semibold text-[#7c3aed] underline"
                    onClick={handleGoToUpgrade}
                  >
                    upgrade
                  </span>
                  .
                </>
              ) : (
                <> — the most any plan allows. Remove one to swap it in.</>
              )}
            </p>
          )}

          {answers.keywords.filter((k) => !question.suggestions?.some((s) => s.toLowerCase() === k.toLowerCase())).length > 0 && (
            <div className="flex flex-wrap gap-2">
              {answers.keywords
                .filter((k) => !question.suggestions?.some((s) => s.toLowerCase() === k.toLowerCase()))
                .map((k) => (
                  <span
                    key={k}
                    className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-[13px] text-gray-700"
                  >
                    {k}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => removeKeyword(k)}
                      aria-label={`Remove ${k}`}
                    />
                  </span>
                ))}
            </div>
          )}
        </div>
      )}

      {/* ---------------- choice (who gets it) ---------------- */}
      {question.kind === 'choice' && (
        <div className="space-y-2">
          {question.choices?.map((choice, index) => {
            const selected = answers.follow_gate === choice.value;
            return (
              <div
                key={choice.value}
                role="button"
                onClick={() =>
                  setAnswers((prev) => ({
                    ...prev,
                    follow_gate: choice.value as ZapGoalAnswers['follow_gate'],
                  }))
                }
                className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-all ${
                  selected ? 'border-[#7c3aed] bg-[#f5f3ff]' : 'border-gray-200 bg-white hover:border-purple-300'
                }`}
              >
                <span
                  className={`mt-[2px] flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                    selected ? 'border-[#7c3aed]' : 'border-gray-300'
                  }`}
                >
                  {selected && <span className="h-2 w-2 rounded-full bg-[#7c3aed]" />}
                </span>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="mb-0 text-[14px] font-semibold text-[#111]">{choice.title}</p>
                    {index === 0 && (
                      <span className="rounded-full bg-[#ecfdf5] px-2 py-[1px] text-[10px] font-semibold text-[#047857]">
                        Recommended
                      </span>
                    )}
                  </div>
                  <p className="mb-0 mt-0.5 text-[12px] text-gray-500">{choice.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ---------------- the card they tap ---------------- */}
      {question.kind === 'opener' && (
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="mb-0 text-[12px] font-semibold text-gray-600">
              {isFollowCard ? 'How you ask them to follow' : 'What the card says'}
            </p>
            <Textarea
              rows={3}
              value={isFollowCard ? answers.follow_ask_message : answers.opener_message}
              onChange={(e) =>
                setAnswers((prev) =>
                  isFollowCard
                    ? { ...prev, follow_ask_message: e.target.value }
                    : { ...prev, opener_message: e.target.value }
                )
              }
            />
            <ChipPicker
              options={isFollowCard ? FOLLOW_ASK_SUGGESTIONS : OPENER_MESSAGE_SUGGESTIONS}
              onPick={(v) =>
                setAnswers((prev) =>
                  isFollowCard ? { ...prev, follow_ask_message: v } : { ...prev, opener_message: v }
                )
              }
            />
          </div>

          <div className="space-y-2">
            <p className="mb-0 text-[12px] font-semibold text-gray-600">
              What the button says <span className="font-normal text-gray-400">(up to 20 characters)</span>
            </p>
            <Input
              value={answers.opener_button_label}
              maxLength={20}
              onChange={(e) => setAnswers((prev) => ({ ...prev, opener_button_label: e.target.value }))}
            />
            <ChipPicker
              options={OPENER_BUTTON_SUGGESTIONS}
              onPick={(v) => setAnswers((prev) => ({ ...prev, opener_button_label: v }))}
            />
          </div>
        </div>
      )}

      {/* ---------------- the message + its link button ---------------- */}
      {/* Fields follow the DM itself: the message sits above the button, so it
          is asked for first. It used to ask for the link first on link-led
          goals, which contradicted this screen's own "with whatever you write
          above it". */}
      {question.kind === 'delivery' && (
        <div className="space-y-4">
          <div className="space-y-2">
            <p className="mb-0 text-[12px] font-semibold text-gray-600">Your message</p>
            <Textarea
              rows={3}
              placeholder={DEFAULT_DM_MESSAGE}
              value={answers.message}
              onChange={(e) => setAnswers((prev) => ({ ...prev, message: e.target.value }))}
            />
            <ChipPicker
              options={MESSAGE_SUGGESTIONS}
              onPick={(v) => setAnswers((prev) => ({ ...prev, message: v }))}
            />
          </div>

          <div className="space-y-2">
            <p className="mb-0 text-[12px] font-semibold text-gray-600">
              {question.linkRequired ? 'The link' : 'Add a link button'}{' '}
              {!question.linkRequired && <span className="font-normal text-gray-400">(optional)</span>}
            </p>
            <Input
              value={answers.link_url}
              placeholder="https://"
              onChange={(e) => setAnswers((prev) => ({ ...prev, link_url: e.target.value }))}
            />
            {linkLooksWrong && (
              <p className="mb-0 text-xs text-red-500">Links need to start with http:// or https://</p>
            )}
          </div>

          {!!answers.link_url.trim() && (
            <div className="space-y-2">
              <p className="mb-0 text-[12px] font-semibold text-gray-600">
                What the link button says <span className="font-normal text-gray-400">(up to 20 characters)</span>
              </p>
              <Input
                value={answers.link_label}
                maxLength={20}
                placeholder="Open link"
                onChange={(e) => setAnswers((prev) => ({ ...prev, link_label: e.target.value }))}
              />
              <ChipPicker
                options={['Open link', 'Get it here', 'Shop now', 'Read more']}
                onPick={(v) => setAnswers((prev) => ({ ...prev, link_label: v }))}
              />
            </div>
          )}
        </div>
      )}

      {/* ---------------- collect sign-ups: link or built form ---------------- */}
      {question.kind === 'signup' && (
        <div className="space-y-4">
          {/* Ordered as the follower experiences it: the message arrives, and the
              button to leave their details sits inside it. */}
          <div className="space-y-2">
            <p className="mb-0 text-[12px] font-semibold text-gray-600">Your message</p>
            <Textarea
              rows={3}
              placeholder={DEFAULT_DM_MESSAGE}
              value={answers.message}
              onChange={(e) => setAnswers((prev) => ({ ...prev, message: e.target.value }))}
            />
            <ChipPicker
              options={MESSAGE_SUGGESTIONS}
              onPick={(v) => setAnswers((prev) => ({ ...prev, message: v }))}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {[
              { mode: 'link' as const, Icon: Link2, title: 'I have a link', copy: 'A Google Form, Typeform, your own page' },
              { mode: 'form' as const, Icon: FileText, title: 'Build a form here', copy: 'We host it — answers come back to you' },
            ].map(({ mode, Icon, title, copy }) => {
              const selected = answers.signup_mode === mode;
              const locked = mode === 'form' && !canBuildForms;
              return (
                <div
                  key={mode}
                  role="button"
                  onClick={() => {
                    if (locked) { handleGoToUpgrade(); return; }
                    setAnswers((prev) => ({
                      ...prev,
                      signup_mode: mode,
                      // 'Open link' is the untouched default and reads wrong on a
                      // form; anything they typed themselves is left alone.
                      link_label:
                        mode === 'form' && (!prev.link_label.trim() || prev.link_label.trim() === 'Open link')
                          ? DEFAULT_SIGNUP_BUTTON
                          : prev.link_label,
                    }));
                  }}
                  className={`relative flex cursor-pointer items-start gap-2 rounded-xl border p-3 transition-all ${
                    selected ? 'border-[#7c3aed] bg-[#f5f3ff]' : 'border-gray-200 bg-white hover:border-purple-300'
                  } ${locked ? 'opacity-70' : ''}`}
                >
                  <Icon className={`mt-[2px] h-4 w-4 flex-shrink-0 ${selected ? 'text-[#7c3aed]' : 'text-gray-400'}`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="mb-0 text-[13px] font-semibold text-[#111]">{title}</p>
                      {locked && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-[#f3f0ff] px-2 py-[1px] text-[10px] font-semibold text-[#7c3aed]">
                          <Lock className="h-[10px] w-[10px]" /> Upgrade
                        </span>
                      )}
                    </div>
                    <p className="mb-0 mt-0.5 text-[11px] text-gray-500">{copy}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {answers.signup_mode === 'link' ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="mb-0 text-[12px] font-semibold text-gray-600">The link</p>
                <Input
                  value={answers.link_url}
                  placeholder="https://"
                  onChange={(e) => setAnswers((prev) => ({ ...prev, link_url: e.target.value }))}
                />
                {linkLooksWrong && (
                  <p className="mb-0 text-xs text-red-500">Links need to start with http:// or https://</p>
                )}
              </div>

              {!!answers.link_url.trim() && (
                <div className="space-y-2">
                  <p className="mb-0 text-[12px] font-semibold text-gray-600">
                    What the button says <span className="font-normal text-gray-400">(up to 20 characters)</span>
                  </p>
                  <Input
                    value={answers.link_label}
                    maxLength={20}
                    placeholder={DEFAULT_SIGNUP_BUTTON}
                    onChange={(e) => setAnswers((prev) => ({ ...prev, link_label: e.target.value }))}
                  />
                  <ChipPicker
                    options={SIGNUP_BUTTON_SUGGESTIONS}
                    onPick={(v) => setAnswers((prev) => ({ ...prev, link_label: v }))}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="mb-0 text-[12px] font-semibold text-gray-600">What are they signing up for?</p>
                <Input
                  value={answers.form_title}
                  placeholder="e.g. Join the waitlist"
                  onChange={(e) => setAnswers((prev) => ({ ...prev, form_title: e.target.value }))}
                />
                <Input
                  value={answers.form_description}
                  placeholder="One line of detail (optional)"
                  onChange={(e) => setAnswers((prev) => ({ ...prev, form_description: e.target.value }))}
                />
              </div>

              <div className="space-y-2">
                <p className="mb-0 text-[12px] font-semibold text-gray-600">What should you ask them?</p>

                {/* Presets first — each already knows its keyboard and validation,
                    so "Email" is never just a text box. */}
                <div className="flex flex-wrap gap-2">
                  {FORM_FIELD_PRESETS.map((preset) => {
                    const added = answers.form_fields.some((f: ZapFormFieldDraft) => f.key === preset.key);
                    return (
                      <button
                        key={preset.key}
                        type="button"
                        onClick={() =>
                          setAnswers((prev) => ({
                            ...prev,
                            form_fields: added
                              ? prev.form_fields.filter((f: ZapFormFieldDraft) => f.key !== preset.key)
                              : [...prev.form_fields, { ...preset }],
                          }))
                        }
                        className={`rounded-full border px-3 py-1.5 text-[12px] font-semibold transition-all ${
                          added
                            ? 'border-[#7c3aed] bg-[#f5f3ff] text-[#7c3aed]'
                            : 'border-gray-200 bg-white text-gray-600 hover:border-purple-300'
                        }`}
                      >
                        {added && <Check className="mr-1 inline h-3 w-3" />}
                        {preset.label}
                      </button>
                    );
                  })}
                </div>

                {/* The questions, in the order the follower will answer them. */}
                {answers.form_fields.map((field: ZapFormFieldDraft, index: number) => (
                  <div key={field.key} className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2">
                    <span className="text-[11px] font-semibold text-gray-400">{index + 1}</span>
                    <div className="min-w-0 flex-1">
                      <p className="mb-0 truncate text-[13px] text-[#111]">{field.label}</p>
                      <button
                        type="button"
                        onClick={() =>
                          setAnswers((prev) => ({
                            ...prev,
                            form_fields: prev.form_fields.map((f: ZapFormFieldDraft) =>
                              f.key === field.key ? { ...f, required: !f.required } : f
                            ),
                          }))
                        }
                        className="border-0 bg-transparent p-0 text-[11px] text-gray-400 hover:text-[#7c3aed]"
                      >
                        {field.required ? 'Required — tap to make optional' : 'Optional — tap to require'}
                      </button>
                    </div>
                    <X
                      className="h-3.5 w-3.5 flex-shrink-0 cursor-pointer text-gray-400"
                      onClick={() =>
                        setAnswers((prev) => ({
                          ...prev,
                          form_fields: prev.form_fields.filter((f: ZapFormFieldDraft) => f.key !== field.key),
                        }))
                      }
                    />
                  </div>
                ))}

                <div className="flex gap-2">
                  <Input
                    value={questionDraft}
                    placeholder="Ask something else, e.g. Which size?"
                    onChange={(e) => setQuestionDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && questionDraft.trim()) {
                        e.preventDefault();
                        setAnswers((prev) => ({ ...prev, form_fields: [...prev.form_fields, newCustomFormField(questionDraft)] }));
                        setQuestionDraft('');
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!questionDraft.trim()) return;
                      setAnswers((prev) => ({ ...prev, form_fields: [...prev.form_fields, newCustomFormField(questionDraft)] }));
                      setQuestionDraft('');
                    }}
                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-black text-white"
                    aria-label="Add question"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <p className="mb-0 text-[12px] font-semibold text-gray-600">
                  What the button says <span className="font-normal text-gray-400">(up to 20 characters)</span>
                </p>
                <Input
                  value={answers.link_label}
                  maxLength={20}
                  placeholder={DEFAULT_SIGNUP_BUTTON}
                  onChange={(e) => setAnswers((prev) => ({ ...prev, link_label: e.target.value }))}
                />
                <ChipPicker
                  options={SIGNUP_BUTTON_SUGGESTIONS}
                  onPick={(v) => setAnswers((prev) => ({ ...prev, link_label: v }))}
                />
              </div>

              <div className="space-y-2">
                <p className="mb-0 text-[12px] font-semibold text-gray-600">
                  What they see after submitting <span className="font-normal text-gray-400">(optional)</span>
                </p>
                <Input
                  value={answers.form_success_message}
                  placeholder="Thanks! We have got your details."
                  onChange={(e) => setAnswers((prev) => ({ ...prev, form_success_message: e.target.value }))}
                />
              </div>

              <p className="mb-0 text-[11px] text-gray-400">
                We create the page when you tap Go Live, and the button in the DM points at it.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ---------------- public replies (many, rotated) ---------------- */}
      {question.kind === 'replies' && (
        <div className="space-y-3">
          {answers.comment_replies.filter((r) => r.trim()).map((r, i) => (
            <div key={i} className="flex items-start gap-2 rounded-lg bg-gray-50 px-3 py-2">
              <p className="mb-0 flex-1 text-[13px] text-gray-700">{r}</p>
              <X
                className="mt-[3px] h-3 w-3 flex-shrink-0 cursor-pointer text-gray-400"
                onClick={() =>
                  setAnswers((prev) => ({
                    ...prev,
                    comment_replies: prev.comment_replies.filter((_, idx) => idx !== i),
                  }))
                }
              />
            </div>
          ))}

          <div className="flex gap-2">
            <Input
              value={replyDraft}
              placeholder="Write a reply"
              onChange={(e) => setReplyDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addReply(replyDraft);
                }
              }}
            />
            <button
              type="button"
              onClick={() => addReply(replyDraft)}
              className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-black text-white"
              aria-label="Add reply"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>

          <ChipPicker options={question.suggestions || []} onPick={(v) => addReply(v)} />

          {/* Not a switch. Rotation is simply what happens with more than one
              reply — the old form said so with a permanently disabled toggle,
              which read as a control you were not allowed to touch. At one
              reply this doubles as the nudge to write a second. */}
          {!!repliesRotationNote(answers.comment_replies) && (
            <div className="flex items-start gap-2 rounded-lg bg-[#f5f3ff] px-3 py-2">
              <Shuffle className="mt-[2px] h-3.5 w-3.5 flex-shrink-0 text-[#7c3aed]" />
              <p className="mb-0 text-[12px] text-[#5b21b6]">
                {repliesRotationNote(answers.comment_replies)}
              </p>
            </div>
          )}
        </div>
      )}

      {/* ---------------- zap link products ---------------- */}
      {question.kind === 'products' && (
        <div className="space-y-4">
          <ZapLinkSection
            zapLinks={zapLinks}
            setZapLinks={setZapLinks}
            maxZapProducts={maxZapProducts}
            handleOpenUpgradePopup={handleOpenUpgradePopup}
            onClose={onClose}
            currentPlanName={currentPlanName}
            allowOtherSource={allowOtherSource}
            productImageUploadPath={zapProductImageUploadPath}
          />

          {/* Instagram allows two buttons on the card on top of the product link
              — usually the creator's shop and bio page. */}
          <div className="rounded-xl border border-gray-200 bg-white p-3">
            <p className="mb-0 text-[12px] font-semibold text-gray-600">
              Extra buttons on the card <span className="font-normal text-gray-400">(optional, up to 2)</span>
            </p>
            <p className="mb-2 mt-0.5 text-[11px] text-gray-400">
              They sit under the product link — send people to your shop or bio page too.
            </p>

            {ctaSuggestions.length > 0 && !ctaFirstComplete && (
              <div className="mb-3 flex flex-wrap gap-2">
                {ctaSuggestions.map((sug) => (
                  <button
                    key={sug.url}
                    type="button"
                    onClick={() => applyCtaSuggestion(1, sug)}
                    className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-600 hover:border-purple-300"
                  >
                    + {sug.label}
                  </button>
                ))}
              </div>
            )}

            <div className="space-y-2">
              <Input
                value={answers.zap_cta_label}
                maxLength={20}
                placeholder="Button label, e.g. Shop My Looks"
                onChange={(e) => setAnswers((prev) => ({ ...prev, zap_cta_label: e.target.value }))}
              />
              <Input
                value={answers.zap_cta_url}
                placeholder="https://"
                onChange={(e) => setAnswers((prev) => ({ ...prev, zap_cta_url: e.target.value }))}
              />
            </div>

            {ctaFirstComplete && (
              <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
                {ctaSuggestions.length > 0 && !answers.zap_cta2_label.trim() && (
                  <div className="flex flex-wrap gap-2">
                    {ctaSuggestions
                      .filter((sug) => sug.url !== answers.zap_cta_url.trim())
                      .map((sug) => (
                        <button
                          key={sug.url}
                          type="button"
                          onClick={() => applyCtaSuggestion(2, sug)}
                          className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-600 hover:border-purple-300"
                        >
                          + {sug.label}
                        </button>
                      ))}
                  </div>
                )}
                <Input
                  value={answers.zap_cta2_label}
                  maxLength={20}
                  placeholder="Second button label"
                  onChange={(e) => setAnswers((prev) => ({ ...prev, zap_cta2_label: e.target.value }))}
                />
                <Input
                  value={answers.zap_cta2_url}
                  placeholder="https://"
                  onChange={(e) => setAnswers((prev) => ({ ...prev, zap_cta2_url: e.target.value }))}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ---------------- recap ---------------- */}
      {isReview && (
        <>
          {/* Responses live in a carousel rather than stacked: the editor and the
              preview below belong to whichever one is selected, so only one can
              be on screen at a time without the page becoming a scroll of
              near-identical blocks. */}
          {ruleCount > 1 && (
            <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
              {responseSummaries.map((_, index) => {
                const active = index === activeResponse;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => onSelectResponse(index)}
                    className={`flex-shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-[12px] font-semibold transition-all ${
                      active ? 'bg-[#7c3aed] text-white' : 'bg-[#f5f3ff] text-[#7c3aed] hover:bg-[#ede9fe]'
                    }`}
                  >
                    Response {index + 1}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={onAddAnotherResponse}
                className="flex-shrink-0 whitespace-nowrap rounded-full border border-dashed border-gray-300 px-3 py-1.5 text-[12px] font-semibold text-gray-500 hover:border-purple-300 hover:text-[#7c3aed]"
              >
                + Add
              </button>
            </div>
          )}

          <div className="rounded-xl border border-gray-200 bg-white p-3">
            <div className="mb-1.5 flex items-center gap-2">
              {ruleCount > 1 && (
                <p className="mb-0 text-[11px] font-semibold uppercase tracking-wide text-[#7c3aed]">
                  Response {activeResponse + 1} of {ruleCount}
                </p>
              )}
              {ruleCount > 1 && (
                <button
                  type="button"
                  onClick={() => onRemoveResponse(activeResponse)}
                  aria-label={`Remove response ${activeResponse + 1}`}
                  className="ml-auto border-0 bg-transparent p-0 text-gray-400 hover:text-red-500"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {summaryLines.map((line, i) => (
              <div key={i} className="mb-1.5 flex items-start gap-2 last:mb-0">
                <Check className="mt-[3px] h-3 w-3 flex-shrink-0 text-[#7c3aed]" />
                <p className="mb-0 text-[13px] text-gray-600">{line}</p>
              </div>
            ))}
          </div>

          {/* Everything the rule can hold, editable here rather than asked for
              up front or buried in the full form. */}
          <div className="rounded-xl border border-gray-200 bg-white px-3 py-1">
            <p className="mb-0 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
              Change anything{ruleCount > 1 ? ` in response ${activeResponse + 1}` : ''}
            </p>
            {recapFields(template, answers, entitlements).map((field) => (
              <RecapRow key={field.id} field={field} answers={answers} setAnswers={setAnswers} />
            ))}
          </div>

          {/* One Zap can answer several different words in different ways — this
              is the old "Add Rule", said plainly. */}
          {ruleCount === 1 && (
            <button
              type="button"
              onClick={onAddAnotherResponse}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 bg-white py-2.5 text-[13px] font-semibold text-gray-600 hover:border-purple-300 hover:text-[#7c3aed]"
            >
              <Plus className="h-4 w-4" />
              Answer a different word too
            </button>
          )}
        </>
      )}

      {/* The conversation, drawn. Under every question, so each answer visibly
          changes something. */}
      <ZapPreview
        steps={previewSteps}
        audienceNote={previewAudienceNote(template, answers)}
        compact={!isReview}
        title={isReview && ruleCount > 1 ? `What your follower sees — response ${activeResponse + 1}` : undefined}
      />

      {/* OLD: an escape hatch into StepConfigureRules, the pre-simplification
          rules editor. It dropped the creator out of the guided flow they were
          halfway through and into the old form — a different layout, different
          vocabulary ("rules", "zap links"), and none of the guardrails the
          guided steps added (the keyword cap, the missing-field highlights).
          Anyone who tapped it had good reason to think they had broken
          something. Commented out rather than deleted: onCustomise and
          StepConfigureRules are both still wired, so restoring it is
          uncommenting this block.
      {isReview && (
        <button
          type="button"
          onClick={onCustomise}
          className="border-0 bg-transparent p-0 text-xs font-semibold text-gray-400 hover:text-[#7c3aed] hover:underline"
        >
          Change advanced settings
        </button>
      )} */}
    </div>
  );
}

export default StepGoalSetup;
